/**
 * Display of a multi-day event's daily hours (migration 2026-10-27-1120).
 *
 * `EventDailyHoursList` renders the stored hours ("Sat, Oct 24: 9 AM–5 PM", one per line) and nothing when there
 * are none, so callers keep today's time display as the fallback. `EventDailyHoursNotice` (detail pages) also tells
 * staff when a multi-day event has NO daily hours, so they know the time range shown is first-day start to
 * last-day end -- the gap that left a Saturday uncovered.
 */
import { ClockIcon } from "@heroicons/react/24/outline";
import {
  dailyHoursApplicability,
  formatClock,
  formatDayLabel,
  resolveDailyHours,
  type DayHours,
  type DayHoursEvent,
} from "../../utils/eventDayHours";

export function EventDailyHoursList({
  days,
  compact = false,
  className = "",
  note,
}: {
  days: DayHours[] | null | undefined;
  compact?: boolean;
  className?: string;
  /** Optional trailing note, e.g. "event local time" when the surrounding screen shows another zone. */
  note?: string;
}) {
  if (!days || days.length === 0) {
    return null;
  }
  return (
    <ul
      className={`${compact ? "text-xs" : "text-sm"} text-gray-700 ${className}`}
      aria-label="Hours each day"
      data-testid="event-daily-hours"
    >
      {days.map((d) => (
        <li key={d.service_date} className="whitespace-nowrap">
          <span className={compact ? "" : "font-medium"}>
            {formatDayLabel(d.service_date)}
          </span>
          {compact ? " " : ": "}
          {formatClock(d.opens_at)}–{formatClock(d.closes_at)}
        </li>
      ))}
      {note && <li className="text-gray-400">({note})</li>}
    </ul>
  );
}

/**
 * Detail-page block. With hours: the per-day list. Without hours on a multi-day timed event: a one-line note that
 * the times shown are the first day's start and the last day's end, and (when `canEditHint`) where to set them.
 * Renders nothing for single-day, all-day or TBD events.
 */
export function EventDailyHoursNotice({
  event,
  rows,
  editHint,
}: {
  event: DayHoursEvent;
  rows: DayHours[] | null | undefined;
  editHint?: string;
}) {
  const app = dailyHoursApplicability(event);
  if (!app.ok) {
    return null;
  }
  const days = resolveDailyHours(event, rows);
  if (days) {
    return (
      <div className="mt-2" data-testid="event-daily-hours-block">
        <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" />
          Hours each day (event local time)
        </div>
        <EventDailyHoursList days={days} className="mt-1 space-y-0.5" />
      </div>
    );
  }
  const stale = (rows?.length ?? 0) > 0;
  return (
    <p
      className="mt-2 text-xs text-amber-700"
      data-testid="event-daily-hours-missing"
    >
      {stale
        ? "The daily hours saved for this event no longer match its dates, so they are not shown. "
        : `Runs over ${app.dates.length} days: the times shown are the first day's opening (${formatClock(app.startClock)}) and the last day's closing (${formatClock(app.endClock)}). Hours on the other days are not set. `}
      {editHint ?? ""}
    </p>
  );
}
